import { api } from '~/data/api'
import { IRecordFile } from '~/utils/types'
import { convertDatesToISO } from '../utils/convertDatesToIso'

export type TRawRecordFileResponse = IRecordFile & {
  file_class: string
  report: string
}

export async function getRecordFiles(
  reportId: string,
  categoryId: string,
): Promise<TRawRecordFileResponse[]> {
  const response = await api().get(
    `/record_file?report_id=${reportId}&record_category_id=${categoryId}`,
  )

  return response.data
}

export async function getRecordFilesByClassification(
  reportId: string,
  document_type: string,
): Promise<TRawRecordFileResponse[]> {
  const response = await api().get(`/record_file_by_classification`, {
    params: {
      report_id: reportId,
      document_type: document_type,
    },
  })

  return response.data
}

export async function getRecordFilesNotClassified(
  reportId: string,
): Promise<TRawRecordFileResponse[]> {
  const response = await api().get(`/record_file_not_classified?report_id=${reportId}`)

  return response.data
}

export type UpdateRecordFileRequest = {
  record_file_id: string
  file_name?: string
  file_class_id?: string
  file_class_properties?: Record<string, any>
  tags?: string[]
  additional_comments?: string
  components_affected?: {
    pn: string
    sn: string
  }[]
  use_suggested_filename?: boolean
  record_category_id?: string
}

export async function updateRecordFile(
  reportId: string,
  fileId: string,
  updateData: Partial<UpdateRecordFileRequest>,
): Promise<TRawRecordFileResponse> {
  const formattedUpdateData = {
    ...updateData,
    file_class_properties: convertDatesToISO(updateData.file_class_properties),
  }

  const response = await api().put(`/record_file/${reportId}/${fileId}`, formattedUpdateData)
  return response.data
}

export async function moveCategoryFromRecordFile(
  fileId: string,
  new_record_category_id?: string,
  status_classification?: string,
): Promise<TRawRecordFileResponse> {
  const response = await api().post(`/record_file/${fileId}/change_category`, {
    record_category_id: new_record_category_id,
    status_classification: status_classification,
  })
  return response.data
}

export async function removeUnclassifiedFiles(reportId: string) {
  const response = await api().post(`/report/${reportId}/remove_unclassified`)
  return response.data
}

export async function confirmClassification(reportId: string) {
  const response = await api().post(`/record_file_not_classified/${reportId}/confirm`)
  return response.data
}

export async function getRecordFile(reportId: string, recordFileId: string, getUrl = true) {
  const params = new URLSearchParams({
    get_url: `${getUrl}`,
  }).toString()
  const response = await api().get<
    IRecordFile & {
      file_url: string
    }
  >(`/record_file/${reportId}/${recordFileId}?${params}`)
  return response.data
}

export async function deleteRecordFile(
  reportId: string,
  recordFileId?: string,
): Promise<IRecordFile> {
  const response = await api().delete(`/record_file/${reportId}/${recordFileId}`)
  return response.data
}

export interface UploadFileParams {
  report_id: string
  record_category_id?: string
  file: File
  auto_classify?: boolean
  onUploadProgress?: (progress: number) => void
}

export const uploadRecordFile = async (params: UploadFileParams) => {
  try {
    const formData = new FormData()
    formData.append('report_id', params.report_id)
    params.record_category_id && formData.append('record_category_id', params.record_category_id)
    formData.append('record_file', params.file)
    params.auto_classify && formData.append('auto_classify', 'true')

    const response = await api().post('/record_file', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      onUploadProgress(progressEvent) {
        params.onUploadProgress?.(progressEvent.loaded / progressEvent.total)
      },
    })
    return response.data
  } catch (error) {
    console.error('File upload failed:', error)
    throw error
  }
}

export const uploadRecordFileNotClassified = async (params: UploadFileParams) => {
  try {
    const formData = new FormData()
    formData.append('report_id', params.report_id)
    formData.append('record_file', params.file)

    const response = await api().post('/record_file_not_classified', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })
    return response.data
  } catch (error) {
    console.error('File upload failed:', error)
    throw error
  }
}

export interface GenerateFilenameProps {
  reportId: string
  recordFileId: string
}

export async function generateFilename({
  reportId,
  recordFileId,
}: GenerateFilenameProps): Promise<IRecordFile> {
  const response = await api().post(`/record_file/${reportId}/${recordFileId}/generate_filename`)
  return response.data
}

export async function fillRecordFileWithAi(
  reportId: string,
  recordFileId?: string,
  autoAdd = false,
): Promise<IRecordFile> {
  const response = await api().post(`/record_file/${reportId}/${recordFileId}/fill_with_ai`, {
    auto_add: autoAdd,
  })
  return response.data
}

export interface IMoveRecordFileProps {
  reportId: string
  recordFileId: string
  recordCategoryUUID: string
  fileClassId?: string
}
export async function moveRecordFile(props: IMoveRecordFileProps): Promise<TRawRecordFileResponse> {
  const response = await api().patch(`/record_file/${props.reportId}/${props.recordFileId}/move`, {
    record_file_id: props.recordFileId,
    record_category_uuid: props.recordCategoryUUID,
    file_class_id: props.fileClassId,
  })
  return response.data
}

export async function getPendingRecordFiles(reportId: string): Promise<TRawRecordFileResponse[]> {
  const response = await api().get(`/record_file/pending/${reportId}`)
  return response.data
}
